import { useEffect, useMemo, useState } from "react";
import { format, subDays } from "date-fns";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { listAudit } from "~/services/admin/access";
import { csvBlob, saveBlob } from "~/utils/download";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { COLORS } from "~/ui/tokens";
import { EmptyState, LoadErrorBanner, PersonAvatar } from "../shared/StatePanels";
import { AUDIT_ACTION_LABELS, auditActionLabel, auditTargetLabel, formatAuditChange, formatDateTimeBR, SENSITIVE_ACTIONS } from "./labels";
import { paginationLabels } from "./DenialsTab";

const EXPORT_PAGE = 100;
const EXPORT_CAP = 5000;
const today = () => format(new Date(), "yyyy-MM-dd");
const weekAgo = () => format(subDays(new Date(), 7), "yyyy-MM-dd");

export default function AuditTab({ people = [], companies = [], initialSubject = null }) {
  // subject = pessoa-alvo (entityType=user&entityId), vinda da ficha; actorUserId = filtro "Pessoa" do mockup (quem fez).
  const [filters, setFilters] = useState({ actorUserId: null, subject: initialSubject, action: "", from: weekAgo(), to: today() });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: "loading", rows: [], total: 0 });
  const [revealed, setRevealed] = useState([]);
  const [exporting, setExporting] = useState(false);
  const notify = useNotify();
  const companiesById = useMemo(() => new Map(companies.map((c) => [c.id, c.name])), [companies]);

  // Vindo de "Ver tudo na auditoria" da ficha: troca só a pessoa-alvo (sem refazer a busca quando não mudou).
  const subjectId = initialSubject?.id ?? null;
  useEffect(() => {
    setFilters((f) => ((f.subject?.id ?? null) === subjectId ? f : { ...f, subject: initialSubject }));
    setPage(0);
  }, [subjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const params = useMemo(
    () => ({
      actorUserId: filters.actorUserId || undefined,
      entityType: filters.subject ? "user" : undefined,
      entityId: filters.subject?.id ?? undefined,
      action: filters.action || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    }),
    [filters]
  );

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, status: "loading" }));
    listAudit({ ...params, limit: rowsPerPage, offset: page * rowsPerPage }, { signal: controller.signal }).then(
      (result) => !controller.signal.aborted && setState({ status: "ok", ...result }),
      () => !controller.signal.aborted && setState((s) => ({ ...s, status: "error" }))
    );
    return () => controller.abort();
  }, [params, page, rowsPerPage, attempt]);

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = [];
      let total = Infinity;
      for (let offset = 0; offset < total && rows.length < EXPORT_CAP; offset += EXPORT_PAGE) {
        // eslint-disable-next-line no-await-in-loop
        const result = await listAudit({ ...params, limit: EXPORT_PAGE, offset });
        total = result.total;
        rows.push(...result.rows);
        if (result.rows.length < EXPORT_PAGE) break;
      }
      const lines = [
        ["Quando", "Quem", "E-mail de quem", "Ação", "Alvo", "Antes", "Depois", "IP"],
        ...rows.slice(0, EXPORT_CAP).map((r) => {
          const change = formatAuditChange(r, { companiesById });
          return [
            formatDateTimeBR(r.createdAt), r.actor?.name || "Sistema", r.actor?.email || "", auditActionLabel(r.action, r), auditTargetLabel(r),
            change.before, change.after, r.ip || "",
          ];
        }),
      ];
      saveBlob(csvBlob(lines), `auditoria-${today()}.csv`);
      if (total > EXPORT_CAP) notify({ severity: "info", message: "Exportadas as primeiras 5.000 linhas. Refine os filtros para exportar o resto." });
    } catch (e) {
      notify({ severity: "info", message: "Não foi possível exportar a auditoria. Tente de novo." });
    } finally {
      setExporting(false);
    }
  };

  const toggleReveal = (id) => setRevealed((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", p: 2 }}>
        <TextField select size="small" label="Pessoa" value={filters.actorUserId ?? ""} onChange={(e) => setFilter("actorUserId", e.target.value ? Number(e.target.value) : null)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ minWidth: 200 }}>
          <option value="">Todas</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
        </TextField>
        {filters.subject && (
          <Chip
            label={`Sobre: ${filters.subject.name}`}
            onDelete={() => setFilter("subject", null)}
            deleteIcon={<CancelIcon aria-hidden={false} aria-label={`Remover filtro Sobre: ${filters.subject.name}`} role="button" />}
            sx={{ bgcolor: "#e0f5fb", color: COLORS.primary, fontWeight: 600 }}
          />
        )}
        <TextField select size="small" label="Ação" value={filters.action} onChange={(e) => setFilter("action", e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ minWidth: 240 }}>
          <option value="">Todas as ações</option>
          {Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </TextField>
        <Box role="group" aria-label="Período" sx={{ display: "flex", gap: 1 }}>
          <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setFilter("from", e.target.value)} />
          <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setFilter("to", e.target.value)} />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <LoadingButton startIcon={<DownloadIcon />} loading={exporting} onClick={exportCsv}>Exportar CSV</LoadingButton>
      </Box>
      {state.status === "error" ? (
        <Box sx={{ px: 2 }}><LoadErrorBanner message="Não foi possível carregar a auditoria. Tente novamente em instantes." onRetry={() => setAttempt((n) => n + 1)} /></Box>
      ) : state.status === "ok" && state.total === 0 ? (
        <EmptyState icon={ManageSearchIcon} title="Nada na auditoria" text="Nenhuma ação encontrada com esses filtros." />
      ) : (
        <>
          <Table size="small" aria-label="Auditoria">
            <TableHead>
              <TableRow>
                {["Quando", "Quem", "Ação", "Alvo", "Antes → Depois"].map((h) => <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {state.status === "loading" && state.rows.length === 0
                ? Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 5 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)
                : state.rows.map((row) => {
                    const sensitive = SENSITIVE_ACTIONS.includes(row.action);
                    const reveal = revealed.includes(row.id);
                    const change = formatAuditChange(row, { reveal, companiesById });
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTimeBR(row.createdAt)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <PersonAvatar name={row.actor?.name || "Sistema"} email={row.actor?.email} size={24} />
                            <span>{row.actor?.name || "Sistema"}</span>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {sensitive && <WarningAmberIcon sx={{ fontSize: 16, color: "#ef6c00", mr: 0.5, verticalAlign: "text-bottom" }} titleAccess="Ação sensível" />}
                          {auditActionLabel(row.action, row)}
                        </TableCell>
                        <TableCell>{auditTargetLabel(row)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
                            <Typography component="s" variant="body2" sx={{ color: COLORS.muted }}>{change.before}</Typography>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: COLORS.muted }} aria-hidden />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{change.after}</Typography>
                            {sensitive && <Button size="small" onClick={() => toggleReveal(row.id)} sx={{ minWidth: 0, p: 0, textTransform: "none" }}>{reveal ? "ocultar" : "mostrar"}</Button>}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={state.total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            {...paginationLabels}
          />
        </>
      )}
    </Box>
  );
}
